import * as React from 'react';
import { useContext, useEffect, useState } from "react";
import { Box, Button, TextField, Typography, List, ListItem, ListItemSecondaryAction, IconButton, ListItemText, Alert, Tabs, Tab, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { colorText, colorWarningBackground, styleSecondaryButton } from "../App/theme.js";
import {
    getKBShares,
    shareKBWith,
    unshareKBWith,
    getAPIKeys,
    deleteAPIKey,
    createAPIKey
} from "../API/KB/KBAPI.js";
import { emailToUserId } from "../App/cryptoUtils.js";
import LocalLoader from "../Loaders/LocalLoader.js";
import { isMobile } from "../App/utils";
import { KBName } from "../names";
import { buttonVerticalMargin } from "../App/style";
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertContext } from "../App/App";
import { CopyToClipboard } from "react-copy-to-clipboard";
import {useConfirm} from "../Components/ConfirmContext";

export function AccessControl({ kbId, title, AESKey }) {
    const [email, setEmail] = useState("");
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [isLoading, setLoading] = useState(false);
    const [KBShares, setKBShares] = useState([]);
    const [apiKeyName, setApiKeyName] = useState("");
    const [apiKeys, setApiKeys] = useState([]);
    const [tabIndex, setTabIndex] = useState(0);
    const [apiKeyDialogOpen, setApiKeyDialogOpen] = useState(false);
    const [newApiKey, setNewApiKey] = useState("");
    const [showApiKey, setShowApiKey] = useState(false);
    const showConfirm = useConfirm();
    const navigate = useNavigate();
    const location = useLocation();
    const { setSystemAlert } = useContext(AlertContext);
    const reset = () => {
        setError('');
        setSuccess('');
        setEmail('');
        setApiKeyName('');
    }

    const handleDelete = async (email) => {
        const confirmed = await showConfirm({
            title: 'Revoke Access Confirmation',
            content: `Are you sure you want to revoke access for ${email}?`
        })
        if (confirmed) {
            setLoading(true);
            await unshareKBWith(emailToUserId(email));
            setKBShares(KBShares.filter((e) => e !== email));
            reset();
            setLoading(false);
        }
    }

    const handleDeleteAPIKey = async (keyId) => {
        const confirmed = await showConfirm({
            title: 'Delete API key Confirmation',
            content: `Are you sure you want to delete this API key?`
        })
        if (confirmed) {
            setLoading(true);
            await deleteAPIKey(keyId);
            setApiKeys(apiKeys.filter((key) => key.apiKey !== keyId));
            setLoading(false);
        }
    }

    const fetchShares = () => {
        setLoading(true);
        getKBShares().then((res) => {
            const { sharedWith } = res;
            setKBShares(sharedWith);
            setLoading(false);
        });
    }

    const fetchAPIKeys = () => {
        setLoading(true);
        getAPIKeys().then((res) => {
            setApiKeys(res);
            setLoading(false);
        });
    }

    useEffect(() => {
        fetchShares();
        fetchAPIKeys();
    }, [kbId]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('subtab');
        if (tab) {
            setTabIndex(parseInt(tab, 10));
        }
    }, [location.search]);

    if (!title) return null;

    const handleSubmit = async () => {
        try {
            setLoading(true);
            const res = await shareKBWith(emailToUserId(email));
            setLoading(false);
            setError('');
            reset();
            fetchShares();
        } catch (error) {
            setLoading(false);
            const errorMessage = error.response && error.response.data && error.response.data.error
                ? error.response.data.error
                : error.message || error.toString();
            if (errorMessage.startsWith('Error: User does not exist')) {
                setError(`User ${email} does not exist`);
            } else if (errorMessage.startsWith('Error: App is already share')) {
                setError(`App is already shared with ${email}`);
            } else {
                setError(errorMessage);
            }
        }
    };

    const handleCreateAPIKey = async () => {
        try {
            setLoading(true);
            const { apiKey } = await createAPIKey(apiKeyName);
            setNewApiKey(apiKey);
            setApiKeyDialogOpen(true);
            setLoading(false);
            setError('');
            reset();
            fetchAPIKeys();
        } catch (error) {
            setLoading(false);
            setError(error.message || error.toString());
        }
    };

    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('subtab', newValue);
        const newUrl = `${location.pathname}?${searchParams.toString()}`;
        navigate(newUrl);
    };

    const handleCloseDialog = () => {
        setApiKeyDialogOpen(false);
        setNewApiKey("");
        setShowApiKey(false);
    };

    const toggleShowApiKey = () => {
        setShowApiKey(!showApiKey);
    };

    return (
        <Box sx={{ padding: 2, color: colorText }}>
            <LocalLoader open={isLoading} blocking={true} />
            <Typography variant="h6" sx={{ marginBottom: 2 }}>Manage Access for "{title}"</Typography>

            <Alert severity="info" sx={{ marginBottom: 4, marginTop: 4 }}>
                Keep in mind that granting full access means the recipient will have the same access level as you do.
                {!isMobile && ` This includes full read and write access, the ability to delete the application, modify its source code, alter or delete data, and unrestricted spending from the associated credits account.`}
            </Alert>

            <Tabs value={tabIndex} onChange={handleTabChange}>
                <Tab label="Collaborators" style={{ textTransform: 'none' }} />
                <Tab label="API Keys" style={{ textTransform: 'none' }} />
                <Tab label="Encryption Key" style={{ textTransform: 'none' }} />
            </Tabs>
            <br />
            {tabIndex === 0 && (
                <Box>
                    <TextField
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter' && email.length > 0) { handleSubmit(); } }}
                        value={email}
                        style={{ marginTop: '32px' }}
                        autoFocus
                        margin="dense"
                        id="name"
                        label={success ? success : 'User Email Address'}
                        type="email"
                        fullWidth
                        variant="standard"
                        color={success ? "success" : ''}
                        error={error.length > 0}
                        helperText={error}
                    />
                    <Box sx={{ marginTop: buttonVerticalMargin, float: (isMobile ? undefined : "right") }}>
                        <Button
                            variant="contained"
                            fullWidth={isMobile}
                            color="secondary"
                            sx={{ marginRight: "16px", width: isMobile ? null : '220px', marginBottom: isMobile ? 2 : 0 }}
                            disabled={!email.length}
                            onClick={handleSubmit}
                        >
                            Grant Full Access
                        </Button>
                    </Box>
                    {KBShares.length > 0 && (
                        <Box sx={{ marginTop: isMobile ? '60px' : '100px' }}>
                            <Typography variant="h6">User Access List:</Typography>
                            <List style={{ marginBottom: '16px' }}>
                                {KBShares.map((email) => (
                                    <ListItem key={email}>
                                        <ListItemText primary={email} />
                                        <ListItemSecondaryAction>
                                            <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(email)}>
                                                <DeleteIcon fontSize={"small"} />
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    )}
                </Box>
            )}
            {tabIndex === 1 && (
                <Box>
                    <TextField
                        onChange={(e) => setApiKeyName(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter' && apiKeyName.length > 0) { handleCreateAPIKey() } }}
                        value={apiKeyName}
                        style={{ marginTop: '32px' }}
                        autoFocus
                        margin="dense"
                        id="apiKeyName"
                        label="API Key Name"
                        type="text"
                        fullWidth
                        variant="standard"
                    />
                    <Box sx={{ marginTop: buttonVerticalMargin, float: (isMobile ? undefined : "right") }}>
                        <Button
                            variant="contained"
                            fullWidth={isMobile}
                            color="secondary"
                            sx={{ marginRight: "16px", width: isMobile ? null : '220px', marginBottom: isMobile ? 2 : 0 }}
                            disabled={!apiKeyName.length}
                            onClick={handleCreateAPIKey}
                        >
                            Create API Key
                        </Button>
                    </Box>
                    {apiKeys.length > 0 && (
                        <Box sx={{ marginTop: '120px' }}>
                            <Typography variant="h6">API Keys:</Typography>
                            <List style={{ marginBottom: '16px' }}>
                                {apiKeys.map((key) => (
                                    <ListItem key={key.apiKey}>
                                        <ListItemText primary={key.name} secondary={`Created on: ${new Date(key.createdAt).toLocaleString()}`} />
                                        <ListItemSecondaryAction>
                                            <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteAPIKey(key.apiKey)}>
                                                <DeleteIcon fontSize={"small"} />
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    )}
                </Box>
            )}

            {tabIndex === 2 && (
                <Box>
                    <Alert severity="warning" sx={{ marginBottom: 4, marginTop: 4 }}>
                        This is your encryption key. Keep it secure and don't share it with anyone you don't trust completely.
                        Anyone with this key can decrypt all data in this application.
                    </Alert>

                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 4 }}>
                        <TextField
                            fullWidth
                            label="Encryption Key"
                            variant="outlined"
                            value={AESKey || ''}
                            type={showApiKey ? "text" : "password"}
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <IconButton
                                        onClick={toggleShowApiKey}
                                        edge="end"
                                        sx={{ mr: 1 }}
                                    >
                                        {showApiKey ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                ),
                            }}
                        />
                    </Box>

                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <CopyToClipboard text={AESKey || ''}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setSystemAlert({ msg: 'Encryption Key Copied', type: 'success', duration: 1500 })}
                                sx={{ width: isMobile ? '100%' : '200px' }}
                            >
                                Copy Encryption Key
                            </Button>
                        </CopyToClipboard>
                    </Box>
                </Box>
            )}

            <Dialog open={apiKeyDialogOpen} onClose={handleCloseDialog}>
                <DialogTitle>API Key Created</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <Alert severity="success" sx={{ marginBottom: 4, marginTop: 4 }}>
                            Your new API key has been created. Please copy and save it securely. This key will not be shown again.
                        </Alert>

                    </DialogContentText>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TextField
                            autoFocus
                            margin="dense"
                            id="apiKey"
                            label="API Key"
                            type={showApiKey ? "text" : "password"}
                            fullWidth
                            variant="standard"
                            value={newApiKey}
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                        <IconButton onClick={toggleShowApiKey} edge="end">
                            {showApiKey ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <CopyToClipboard text={newApiKey}>
                        <Button variant="contained" sx={{ mr: 1, width: 120 }}
                                onClick={() => setSystemAlert({ msg: 'API Key Copied', type: 'success', duration: 1500 })}>
                            Copy API Key
                        </Button>
                    </CopyToClipboard>
                    <Button variant="outlined" onClick={handleCloseDialog} color="primary" sx={{ mr: 1, width: 120 }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}